import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  FormHelperText,
  InputLabel,
  Link,
  OutlinedInput,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { MarkEmailRead } from '@mui/icons-material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { authClient } from '../../lib/auth-client';
import Logo from '../../assets/images/logo.svg';

const schema = Yup.object({
  email: Yup.string().email('Must be a valid email').required('Email is required'),
});

function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState('');

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: schema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      const { error } = await authClient.requestPasswordReset({
        email: values.email,
        redirectTo: window.location.origin + '/reset-password',
      });

      if (error) {
        // Don't reveal whether an account exists — show generic success anyway.
        // Only surface real errors (e.g. rate limit).
        if (error.status === 429) {
          setErrors({ email: 'Too many requests. Please wait a moment.' });
          setSubmitting(false);
          return;
        }
      }

      setSentTo(values.email);
      setSent(true);
      setSubmitting(false);
    },
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 2 }}>
      <Paper
        sx={{
          p: 4,
          width: 400,
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(15,23,42,0.10)',
          border: '1px solid #E2E8F0',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: 3,
            background: '#0F172A',
          },
        }}>
        <Stack spacing={3} alignItems="center" mb={3}>
          <img src={Logo} alt="5milah" width={52} height={52} />
          {!sent ? (
            <Stack spacing={0.5} alignItems="center">
              <Typography variant="h5" fontWeight={700}>Forgot password?</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Enter your email and we'll send you a reset link.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={0.5} alignItems="center">
              <MarkEmailRead sx={{ fontSize: 40, color: 'success.main', mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700}>Check your inbox</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                If <strong>{sentTo}</strong> is registered, a reset link is on its way.
                The link expires in 1 hour.
              </Typography>
            </Stack>
          )}
        </Stack>

        {!sent ? (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2.5}>
              <Stack spacing={0.75}>
                <InputLabel htmlFor="fp-email" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                  Email
                </InputLabel>
                <OutlinedInput
                  id="fp-email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter your email"
                  fullWidth
                  error={!!(formik.touched.email && formik.errors.email)}
                  autoFocus
                />
                {formik.touched.email && formik.errors.email && (
                  <FormHelperText error>{formik.errors.email}</FormHelperText>
                )}
              </Stack>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={formik.isSubmitting}
                sx={{ py: 1.25, fontWeight: 600 }}>
                {formik.isSubmitting ? 'Sending…' : 'Send Reset Link'}
              </Button>
            </Stack>
          </form>
        ) : (
          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            fullWidth
            size="large"
            sx={{ fontWeight: 600 }}>
            Back to Sign In
          </Button>
        )}

        {!sent && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/login" variant="body2" color="text.secondary">
              Back to Sign In
            </Link>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ForgotPasswordPage;
